import React from "react";
import { Phone } from "lucide-react";
import { Home, Building2, UserRound, Search } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LanguageSelector } from "./ui/LanguageSelector";
import { useDoctors } from "../hooks/useDoctors";
import { useClinics } from "../hooks/useClinics";
import { scrollToTop } from "../utils/scrollToTop";
import { ClinicScheduleStatusDto } from "../web-api-client";
import { SearchOverlay } from "./ui/SearchOverlay";
import { Icon } from "@iconify/react";
import { useLanguage } from "../i18n/LanguageContext";
import HospitalLogoDesktop from "./landing/HospitalLogoDesktop";
import { appConfig } from "../appconfig";

export function Header({ isMobile }: { isMobile?: boolean }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useLanguage();

  const phoneRaw = appConfig.supportPhone ?? "";

  const navItems = [
    { name: t("trangchu"), icon: Home, href: "/" },
    { name: t("phongkham"), icon: Building2, href: "/phong-kham" },
    { name: t("bacsi"), icon: UserRound, href: "/bac-si" },
    { name: t("tracuuphieukham"), icon: Search, href: "/phieu-kham" },
  ];

  const navItemsActive = navItems.map((item) => ({
    ...item,
    isActive:
      item.href !== "#" &&
      (pathname === item.href || pathname.startsWith(item.href + "/")),
  }));

  const { doctors } = useDoctors({
    date: "",
    specialty: null,
    availableOnly: false,
    searchTerm: "",
  });

  const { data: clinics = [] } = useClinics(true);

  const handleDoctorClicked = (doctorId: string) => {
    const params = new URLSearchParams({ doctorId });
    navigate(`/profile?${params.toString()}`);
    scrollToTop();
  };

  const handleClinicAction = (_clinic: ClinicScheduleStatusDto) => { };

  const shouldHideLogoNav =
    isMobile &&
    (pathname === "/bac-si" ||
      pathname === "/phong-kham" ||
      pathname === "/phieu-kham-benh" ||
      pathname === "/phieu-kham");
  if (isMobile === undefined) return null;

  if (isMobile) {
    return (
      <header className="z-50">
        {shouldHideLogoNav && (
          <div className="h-[42px] bg-[#2f7d44]">
            <div className="relative mx-auto flex h-full max-w-6xl items-center justify-center gap-6">
              <div className="flex h-full items-center gap-2 text-sm">
                <img src="/phone-white.svg" className='w-5 h-5 text-white' />
                <span className="font-semibold text-white">{t("hotrodatkham")}:</span>
                <span className="font-semibold text-white">{phoneRaw}</span>
              </div>

              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <LanguageSelector isMobile={isMobile} />
              </div>
            </div>
          </div>
        )}


        {!shouldHideLogoNav && (
          <div className="flex h-[70px] items-center justify-between border-b border-[#e6efe8] bg-sub-primary px-4">
            <a className="flex h-[90%] items-center shrink-0" href="/">
              <img
                src="/images/logo_108.svg"
                alt="Bệnh viện Trung Ương Quân Đội 108"
                className="h-full w-auto object-contain"
              />
              <img
                src="/images/text_a16.png"
                alt="Bệnh viện Trung Ương Quân Đội 108"
                className="h-[80%] w-auto object-contain"
              />
            </a>

            <div className="flex items-center gap-3">
              {/* <SearchOverlay
                isMobile={isMobile}
                doctors={doctors}
                clinics={clinics}
                onDoctorClicked={handleDoctorClicked}
                onClinicClicked={(clinic) => handleClinicAction(clinic)}
              /> */}
              <LanguageSelector isMobile={isMobile} />
            </div>
          </div>
        )}
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-50">
      <div className="h-[42px] border-b border-white/10 bg-[#2f7d44]">
        <div className="mx-auto flex h-full max-w-7xl items-center justify-end gap-6 px-6">
          <div className="flex h-full items-center gap-2">
            <img src="/phone-white.svg" className='w-6 h-6 text-white' />
            <span className="font-semibold text-white">{t("hotrodatkham")}:</span>
            <a
              href={toTelHref(phoneRaw)}
              className="font-bold text-white hover:opacity-90"
            >
              {phoneRaw}
            </a>
          </div>

          <a
            href="https://www.facebook.com/khoaquoctebv108"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            className="inline-flex items-center justify-center"
          >
            <Icon
              icon="mdi:facebook"
              className="h-5 w-5 text-white transition-opacity duration-200 hover:opacity-70"
            />
          </a>

          <div className="flex h-full items-center">
            <LanguageSelector isMobile={isMobile} />
          </div>
        </div>
      </div>

      <div className="h-[108px] border-b border-[#e6efe8] bg-sub-primary backdrop-blur">
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-6 xl:px-8">
          <a className="flex h-full items-center shrink-0" href="/">
            <img
              src="/images/logo_108.svg"
              alt="Bệnh viện Trung Ương Quân Đội 108"
              className="h-[95%] w-auto object-contain"
            />
            <img
              src="/images/text_a16.png"
              alt="Bệnh viện Trung Ương Quân Đội 108"
              className="h-[60%] w-auto object-contain"
            />
          </a>

          <nav className="ml-8 flex h-full items-center justify-end gap-3 lg:gap-5 xl:gap-8">
            {navItemsActive.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="group relative flex h-full items-center px-2 xl:px-3"
              >
                <span
                  className={`text-[16px] uppercase font-semibold tracking-[0.01em] transition-colors duration-300 ${item.isActive
                    ? "text-[#2f7d44]"
                    : "text-[#2b3b30] group-hover:text-[#2f7d44]"
                    }`}
                >
                  {item.name}
                </span>

                <span
                  className={`absolute bottom-0 left-0 h-[3px] w-full origin-left rounded-full bg-[#8ac56f] transition-transform duration-300 ${item.isActive
                    ? "scale-x-100"
                    : "scale-x-0 group-hover:scale-x-100"
                    }`}
                />
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
}

function toTelHref(raw: string) {
  const only = raw.replace(/\s+/g, "");
  if (only.startsWith("+")) return `tel:${only}`;

  const digits = only.replace(/\D+/g, "");
  const e164 = digits.startsWith("0") ? `+84${digits.slice(1)}` : `+${digits}`;
  return `tel:${e164}`;
}
